import * as vp from 'vopay';
import { Box, Select, DateRangePicker, AVAILABLE_TIMEZONES } from 'vopay';
import { FunctionComponent, useState } from 'react';
import styles from './FileUploadHistory.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';

type BulkUploadSearchParams = {
  paymentType: string;
  transactionType: string;
  timeZone: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
};

type SearchFormProps = {
  paymentType: string;
  transactionType: string;
  timeZone: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  search: (searchParams: BulkUploadSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const BulkUploadForm: FC = ({
  paymentType,
  transactionType,
  hasInvalidEndDate,
  shouldShowEndDateError,
  timeZone,
  setShouldShowEndDateError,
  initialTimeRange,
  search,
}: SearchFormProps) => {
  const [currentPaymentType, setCurrentPaymentType] = useState<string>(paymentType);
  const [currentTransactionType, setCurrentTransactionType] = useState<string>(transactionType);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>(timeZone);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));

  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  const {
    response: { Country },
  } = useClientData();

  const paymentTypeOption = [
    { value: 'all', label: t('PaymentType.Options.all') },
    { value: 'Debit', label: t('PaymentType.Options.debit') },
    { value: 'Credit', label: t('PaymentType.Options.credit') },
  ];

  const transactionTypeOption = [
    { value: 'all', label: 'All' },
    { value: 'EFT', label: 'EFT' },
    { value: 'Paylink', label: 'eLinx™' },
    { value: 'RTR', label: 'VopayInstant' },
    { value: 'Interac', label: 'e-Transfer' },
    { value: 'OCT', label: 'OCT' },
    { value: 'CreditCard', label: t('TransactionType.Options.creditCard') },
  ];

  const transactionTypeOptionUs = [
    { value: 'all', label: 'All' },
    { value: 'ACH', label: 'ACH' },
    { value: 'RTP', label: 'RTP' },
    { value: 'OCT', label: 'OCT' },
    { value: 'CreditCard', label: t('TransactionType.Options.creditCard') },
    { value: 'Wire', label: 'Wire' },
  ];

  return (
    <Box flx="row" gap="sm" wrap="wrap">
      <div className={styles.datePickerContainer}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
          triggerVariant="outlineShadow"
        />
      </div>

      <Box width="200px">
        <Select
          version="2"
          placeholder={t('TransactionType.placeholder')}
          options={Country === 'US' ? transactionTypeOptionUs : transactionTypeOption}
          name="transactionType"
          value={currentTransactionType}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentTransactionType(e.target.value);
          }}
        />
      </Box>

      <Box width="200px">
        <Select
          version="2"
          placeholder={t('PaymentType.placeholder')}
          options={paymentTypeOption}
          name="scheduleType"
          value={currentPaymentType}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentPaymentType(e.target.value);
          }}
        />
      </Box>

      <Box width="260px">
        <Select
          version="2"
          placeholder={t('timeZonePlaceholder')}
          options={timeZoneOptions}
          name="timeZone"
          value={currentTimeZone}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentTimeZone(e.target.value as vp.Timezone);
          }}
        />
      </Box>

      <Button
        id="search-file-history"
        size="small"
        colorScheme="primary"
        onClick={() => {
          search({
            paymentType: currentPaymentType,
            transactionType: currentTransactionType,
            timeZone: currentTimeZone,
            timeRange,
          });
        }}
      >
        {tGlobal('Buttons.search')}
      </Button>
    </Box>
  );
};

export default BulkUploadForm;
